import React, { useState, useEffect } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Box,
  ButtonGroup,
  Icon,
  Text,
} from "@chakra-ui/react";
import { saveKGColumn } from "../../services/DataModelService";
import { Header } from "build-ts/metadata";
import { KGColumnProto } from "build-ts/knowledege_graph";
import {
  ColumnType,
  TimeBucket,
  timeBucketToJSON,
  timeBucketFromJSON,
  AggregationType,
  aggregationTypeToJSON,
  aggregationTypeFromJSON,
} from "build-ts/enums";
import showToast from "hooks/useCustomToast";
import { FiSave, FiX } from "react-icons/fi";
import { iconForKgColumn } from "../../utils/ontologyEntityIcons";

interface CreateKGColumnComponentProps {
  defaultName?: string;
  onClose: () => void;
  colType: ColumnType;
  refreshKGColumns: () => Promise<Header[]>;
  onSuccess?: (createdHeader: Header) => void;
}

const MEASURE_AGGREGATION_OPTIONS: AggregationType[] = [
  AggregationType.SUM,
  AggregationType.AVERAGE,
  AggregationType.COUNT,
  AggregationType.MIN,
  AggregationType.MAX,
];

const CreateKGColumnComponent: React.FC<CreateKGColumnComponentProps> = ({
  defaultName,
  onClose,
  colType,
  refreshKGColumns,
  onSuccess,
}) => {
  const [kgColumnName, setKGColumnName] = useState(defaultName || "");
  const [kgColumnDescription, setKGColumnDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Low cardinality → 1, High → 0 */
  const [lowCardinality, setLowCardinality] = useState(true);
  const [timeBucket, setTimeBucket] = useState<TimeBucket>(TimeBucket.DAILY);
  const [aggregation, setAggregation] = useState<AggregationType>(AggregationType.SUM);

  useEffect(() => {
    setKGColumnName(defaultName || "");
    setKGColumnDescription("");
    setLowCardinality(true);
    setTimeBucket(TimeBucket.DAILY);
    setAggregation(AggregationType.SUM);
  }, [defaultName, colType]);

  const handleSubmit = async () => {
    if (!kgColumnName.trim()) {
      showToast("Name is required", "", "error");
      return;
    }
    setIsSubmitting(true);

    const kgColumnProto = KGColumnProto.create({
      header: Header.create({
        name: kgColumnName,
        description: kgColumnDescription,
      }),
      column_type: colType,
    });

    if (colType === ColumnType.ATTRIBUTE) {
      kgColumnProto.cardinality = lowCardinality ? 1 : 0;
    } else if (colType === ColumnType.MEASURE) {
      kgColumnProto.aggregation = aggregation;
    } else if (colType === ColumnType.TEMPORAL) {
      kgColumnProto.time_bucket = timeBucket;
    }

    const result = await saveKGColumn(kgColumnProto, false);
    if (result) {
      await refreshKGColumns();
      showToast("KG Column created successfully", "", "success");
      onSuccess?.(result.header!);
      setIsSubmitting(false);
      onClose();
    } else {
      setIsSubmitting(false);
    }
  };

  return (
    <Box p={4}>
      <VStack spacing={4} align="stretch">
        <HStack
          spacing={3}
          px={3}
          py={2.5}
          bg="purple.50"
          borderRadius="md"
          borderWidth="1px"
          borderColor="purple.100"
          align="center"
        >
          <Icon as={iconForKgColumn(colType)} boxSize={5} color="purple.600" flexShrink={0} aria-hidden />
          <Box minW={0}>
            <Text fontSize="xs" fontWeight="700" color="purple.700" letterSpacing="wider" mb={0} textTransform="uppercase">
              Entity type
            </Text>
            <Text fontWeight="600" fontSize="sm" color="gray.800" mb={0}>
              {colType === ColumnType.MEASURE ? "Measure" : colType === ColumnType.TEMPORAL ? "Time" : "Attribute"}
            </Text>
          </Box>
        </HStack>

        <FormControl isRequired>
          <FormLabel>Name</FormLabel>
          <Input
            placeholder="Enter name"
            value={kgColumnName}
            onChange={(e) => setKGColumnName(e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Description</FormLabel>
          <Input
            placeholder="Enter description"
            value={kgColumnDescription}
            onChange={(e) => setKGColumnDescription(e.target.value)}
          />
        </FormControl>

        {colType === ColumnType.ATTRIBUTE && (
          <FormControl>
            <FormLabel>Cardinality</FormLabel>
            <ButtonGroup size="sm" isAttached variant="outline">
              <Button
                colorScheme={lowCardinality ? "purple" : "gray"}
                variant={lowCardinality ? "solid" : "outline"}
                onClick={() => setLowCardinality(true)}
              >
                Low
              </Button>
              <Button
                colorScheme={!lowCardinality ? "purple" : "gray"}
                variant={!lowCardinality ? "solid" : "outline"}
                onClick={() => setLowCardinality(false)}
              >
                High
              </Button>
            </ButtonGroup>
          </FormControl>
        )}

        {colType === ColumnType.MEASURE && (
          <FormControl>
            <FormLabel>Default Aggregation</FormLabel>
            <Select
              value={aggregationTypeToJSON(aggregation)}
              onChange={(e) => setAggregation(aggregationTypeFromJSON(e.target.value))}
            >
              {MEASURE_AGGREGATION_OPTIONS.map((a) => (
                <option key={aggregationTypeToJSON(a)} value={aggregationTypeToJSON(a)}>
                  {aggregationTypeToJSON(a)}
                </option>
              ))}
            </Select>
          </FormControl>
        )}

        {colType === ColumnType.TEMPORAL && (
          <FormControl>
            <FormLabel>Time Bucket</FormLabel>
            <Select
              value={timeBucketToJSON(timeBucket)}
              onChange={(e) => setTimeBucket(timeBucketFromJSON(e.target.value))}
            >
              <option value={timeBucketToJSON(TimeBucket.DAILY)}>DAILY</option>
              <option value={timeBucketToJSON(TimeBucket.WEEKLY)}>WEEKLY</option>
              <option value={timeBucketToJSON(TimeBucket.MONTHLY)}>MONTHLY</option>
              <option value={timeBucketToJSON(TimeBucket.QUARTERLY)}>QUARTERLY</option>
              <option value={timeBucketToJSON(TimeBucket.YEARLY)}>YEARLY</option>
            </Select>
          </FormControl>
        )}

        <HStack spacing={3} pt={4} justify="center" w="100%">
          <Button
            colorScheme="green"
            bg="green.600"
            color="white"
            _hover={{ bg: "green.700" }}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            size="md"
            leftIcon={<Icon as={FiSave} boxSize={5} />}
            w="50%"
          >
            <Text mb={0} fontWeight="700">
              Create
            </Text>
          </Button>
          <Button
            onClick={onClose}
            size="md"
            variant="outline"
            colorScheme="purple"
            leftIcon={<Icon as={FiX} boxSize={5} />}
            w="50%"
          >
            <Text mb={0} fontWeight="700">
              Cancel
            </Text>
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default CreateKGColumnComponent;
